import { Injectable } from '@angular/core';
import { MatDateFormats, DateAdapter } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment-timezone';
import 'moment/locale/ca';
import 'moment/locale/en-gb';
import 'moment/locale/es';

export const SUPPORTED_LANGUAGES = ['es', 'en', 'ca'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LANGUAGE_STORAGE_KEY = 'rb-language';
const DATE_LOCALES: Record<AppLanguage, string> = {
  es: 'es-ES',
  en: 'en-GB',
  ca: 'ca-ES',
};

export function storedAngularLocale(): string {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as AppLanguage | null;
  return DATE_LOCALES[stored && SUPPORTED_LANGUAGES.includes(stored) ? stored : 'es'];
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
  constructor(
    private readonly translate: TranslateService,
    private readonly dateAdapter: DateAdapter<Date>,
  ) {
    this.translate.addLangs([...SUPPORTED_LANGUAGES]);
    this.translate.setFallbackLang('es');
  }

  initialize(): void {
    this.use(this.readStoredLanguage() || this.browserLanguage());
  }

  use(language?: string): AppLanguage {
    const selected = this.normalize(language);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, selected);
    document.documentElement.lang = selected;
    this.translate.use(selected);
    this.dateAdapter.setLocale(DATE_LOCALES[selected]);
    moment.locale(selected === 'en' ? 'en-gb' : selected);
    return selected;
  }

  current(): AppLanguage {
    return this.normalize(this.translate.getCurrentLang() || this.readStoredLanguage());
  }

  locale(language = this.current()): string {
    return DATE_LOCALES[language];
  }

  private readStoredLanguage(): string | null {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  }

  private browserLanguage(): AppLanguage {
    return this.normalize(navigator.language?.split('-')[0]);
  }

  private normalize(language?: string | null): AppLanguage {
    return SUPPORTED_LANGUAGES.includes(language as AppLanguage)
      ? (language as AppLanguage)
      : 'es';
  }
}
